import { useContext } from "react"
import {
  DestinationStatuses,
  EventType,
  OrderEventHistoryItem,
  TableOrder,
  getEventTypeTextKey,
} from "@/types/orders.types"
import { VehicleType } from "@/types/vehicle.types"
import { getFailureReasonText } from "@/utils/failureReason"
import { getHumanReadableDateTimeString, humanizeTimestamp } from "@/utils/order-time-calculations"
import { getOrderStatusTextKey, getStopStatusTextKey } from "@/utils/status"
import { faInfoCircle, faLocationDot } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Popover, Table, Tag, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { Driver } from "@/types/drivers.types"
import { CoordinatesLatLng } from "@/types/createSingleOrder.types"
import { defaultGlobalMapState, globalMapAtom } from "@/atoms/globalMapAtom"
import { KosmoMapContext } from "@/modules/shared/Map/MapProvider"
import { useRecoilValue, useSetRecoilState } from "recoil"
import BrandedQuestionMark from "@/UI/Icons/BrandedQuestionMark"
import { getManhattanDistanceInMetersBetweenTheCoords, humanizeDistance } from "@/utils/distance"
import { OrderStatusesTypes } from "@/types/statuses.types"
import { userAtom } from "@/atoms/userAtom"

interface OrderEventHistoryTableProps {
  loading: boolean
  history: OrderEventHistoryItem[]
  order?: TableOrder
  isFocused?: boolean
}

type CoordinateInfo = {
  driver: Driver
  originMapMarker?: CoordinatesLatLng
  destinationMapMarker?: CoordinatesLatLng
  oldDestinationMapMarker?: CoordinatesLatLng
  distanceBetween?: number
}

const OrderEventHistoryTable: React.FC<OrderEventHistoryTableProps> = ({
  loading,
  history,
  order,
  isFocused,
}) => {
  const { t } = useTranslation()
  const mapContext = useContext(KosmoMapContext)
  const setGlobalMapState = useSetRecoilState(globalMapAtom)
  const { preference } = useRecoilValue(userAtom)

  const handleMapOpenChange = (isOpen: boolean, coordinateInfo: CoordinateInfo) => {
    if (isOpen && coordinateInfo) {
      setGlobalMapState((mapState) => ({
        current: {
          compact: true,
          defaultCenter: {
            lat: coordinateInfo.driver.latitude ?? 0,
            lng: coordinateInfo.driver.longitude ?? 0,
          },
          driver: coordinateInfo.driver,
          originMapMarker: coordinateInfo.originMapMarker,
          destinationMapMarker: coordinateInfo.destinationMapMarker,
          oldDestinationMapMarker: coordinateInfo.oldDestinationMapMarker,
        },
        prev: mapState.prev ? mapState.prev : mapState.current,
      }))
    } else {
      setTimeout(
        () =>
          setGlobalMapState((mapState) => ({
            current: !!mapState.prev ? mapState.prev : defaultGlobalMapState,
            prev: null,
          })),
        400
      )
    }
  }

  const columns = [
    {
      title: t("common.event"),
      dataIndex: "event",
      key: "event",
      render: (event: {
        statusText: string
        failureReasonText: string
        additionalText: string
        driverNote: string
      }) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-x-1">
            <p>{event.statusText}</p>
            {!!event.failureReasonText && (
              <Tooltip title={event.failureReasonText}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            )}
            {!!event.driverNote && (
              <Tooltip title={event.driverNote}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            )}
          </div>
          {!!event.additionalText && <p className="text-gray-500">{event.additionalText}</p>}
        </div>
      ),
    },
    {
      title: t("common.time"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (time: string) => <p>{time}</p>,
    },
    {
      title: t("common.driver"),
      dataIndex: "driverName",
      key: "driverName",
      render: (driver: string) => <p>{driver}</p>,
    },
    {
      title: t("common.location"),
      dataIndex: "coordinateInfo",
      key: "coordinateInfo",
      render: (coordinateInfo?: CoordinateInfo) => (
        <div className="flex justify-center items-center">
          {coordinateInfo ? (
            <Popover
              destroyTooltipOnHide
              onOpenChange={(isOpen) => handleMapOpenChange(isOpen, coordinateInfo)}
              content={
                <div className="w-[400px] h-[400px]">
                  {/* Only one instance of the map can be loaded at a time - in conflict with main order details */}
                  {isFocused && mapContext?.KosmoMap && <mapContext.KosmoMap />}
                </div>
              }
              trigger="click"
            >
              <div className="flex flex-col items-center gap-y-2">
                <div className="flex items-center gap-x-2">
                  <FontAwesomeIcon className="cursor-pointer" icon={faLocationDot} />
                </div>
                {(coordinateInfo.distanceBetween ?? 0) >= 250 && (
                  <Tag color="error">
                    ~
                    {humanizeDistance(coordinateInfo.distanceBetween ?? 0, preference.distanceUnit)}
                  </Tag>
                )}
              </div>
            </Popover>
          ) : (
            <p>-</p>
          )}
        </div>
      ),
    },
    {
      title: (
        <Tooltip
          title={<p>{t("common.promisedDeliveryTimeDescription")}</p>}
          overlayStyle={{ zIndex: 3001 }}
          className="flex flex-row items-center gap-x-1"
        >
          <p>{t("common.promisedDeliveryTime")}</p>
          <BrandedQuestionMark className="mb-0.5" />
        </Tooltip>
      ),
      dataIndex: "recipientOriginalDropoffETA",
      key: "pdt",
      render: (time: string) => <p>{time}</p>,
    },
    {
      title: (
        <Tooltip
          title={<p>{t("common.dropoffETADescription")}</p>}
          overlayStyle={{ zIndex: 3001 }}
          className="flex flex-row items-center gap-x-1"
        >
          <p>{`${t("common.order-field.dropoff")} ETA`}</p>
          <BrandedQuestionMark className="mb-0.5" />
        </Tooltip>
      ),
      dataIndex: "recipientDropoffETA",
      key: "dropoffETA",
      render: (time: string) => <p>{time}</p>,
    },
  ]

  const tableData = [...history]
    .sort((item1, item2) => (item1.createdAt < item2.createdAt ? -1 : 1))
    .map((item) => ({
      key: item.id,
      event: {
        statusText:
          item.recipientStatus &&
          item.status !== EventType.DestinationDuplicated &&
          item.status !== EventType.DestinationLocationUpdated
            ? t(getStopStatusTextKey(item.recipientStatus as DestinationStatuses))
            : item.status !== EventType.DeliveryReassigned &&
              item.status !== EventType.DestinationDuplicated &&
              item.status !== EventType.DestinationLocationUpdated
            ? t(getOrderStatusTextKey(item.status))
            : t(getEventTypeTextKey(item.status)),
        failureReasonText: getFailureReasonText(
          t,
          item.recipientFailureReason,
          item.recipientFailureReasonNote
        ),
        driverNote: item.driverNote ?? "",
        additionalText: item.recipientFullName ?? "",
      },
      driverName: item.driverName,
      coordinateInfo:
        !!item.latitude && !!item.longitude
          ? (() => {
              const originMapMarker = !!item.recipientStatus
                ? undefined
                : {
                    lat: order?.sender?.location?.latitude ?? 0,
                    lng: order?.sender?.location?.longitude ?? 0,
                  }
              const destinationMapMarker =
                item.recipientStatus && item.stopIndex !== undefined
                  ? {
                      lat: order?.recipients[item.stopIndex]?.location?.latitude ?? 0,
                      lng: order?.recipients[item.stopIndex]?.location?.longitude ?? 0,
                    }
                  : item.newLocation
                  ? {
                      lat: item.newLocation.latitude,
                      lng: item.newLocation.longitude,
                    }
                  : undefined
              const oldDestinationMapMarker =
                item.status === EventType.DestinationLocationUpdated && item.oldLocation
                  ? {
                      lat: item.oldLocation.latitude,
                      lng: item.oldLocation.longitude,
                    }
                  : undefined
              return {
                driver: {
                  id: "",
                  name: item.driverName ?? "",
                  phone: "",
                  email: "",
                  vehicleType: VehicleType.MOTORCYCLE,
                  latitude: item.latitude,
                  longitude: item.longitude,
                  locationUpdatedAt: Date.now(),
                  status: "online",
                  preventAnimation: true,
                } as Driver,
                originMapMarker,
                destinationMapMarker,
                oldDestinationMapMarker,
                distanceBetween:
                  (item.status === OrderStatusesTypes.PICKED_UP &&
                    [
                      "",
                      DestinationStatuses.CANCELED,
                      DestinationStatuses.DELIVERED,
                      DestinationStatuses.RETURNING,
                    ].includes(item.recipientStatus || "")) ||
                  (item.status !== OrderStatusesTypes.DELIVERED && item.recipientStatus === "") ||
                  (item.status !== OrderStatusesTypes.CANCELLED && item.recipientStatus === "")
                    ? getManhattanDistanceInMetersBetweenTheCoords(
                        item.latitude,
                        item.longitude,
                        originMapMarker?.lat ?? destinationMapMarker?.lat ?? 0,
                        originMapMarker?.lng ?? destinationMapMarker?.lng ?? 0
                      )
                    : 0,
              }
            })()
          : undefined,
      recipientOriginalDropoffETA: item.recipientOriginalDropoffETA
        ? humanizeTimestamp(item.recipientOriginalDropoffETA)
        : "-",
      recipientDropoffETA: item.recipientDropoffETA
        ? humanizeTimestamp(item.recipientDropoffETA)
        : "-",
      createdAt: getHumanReadableDateTimeString(item.createdAt, t),
    }))

  return (
    <Table
      rowKey="key"
      loading={loading}
      columns={columns}
      dataSource={tableData}
      pagination={false}
    />
  )
}

export default OrderEventHistoryTable
